import type { DrawLevel, ModLevels } from '@ps/web-shared-api-types/v1-preview/SwPkrSpinAndGo';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import { PrizeDistributionModalTabs } from '../../../../types';
import { GameType, type GameTypeName } from '../../../../types/Game';
import BlindLevel from '../BlindLevel/BlindLevel';
import PrizePool from '../PrizePool/PrizePool';
import { ProbabilityInfo } from '../ProbabilityInfo/ProbabilityInfo';
import { TimeLimits } from '../TimeLimits/TimeLimits';

import { TXTCLI_No_data_available } from './GameInfo.messages.json';
import styles from './GameInfo.scss';

export interface GameInfoProps {
  gameType: GameTypeName;
  rake: number;
  scalePM: number;
  buyIn: number;
  drawLevels?: DrawLevel[];
  currency: number;
  modLevels: ModLevels[];
  selectedTab: PrizeDistributionModalTabs;
  isSmallScreen: boolean;
}

const GameInfo: React.FC<GameInfoProps> = ({
  gameType,
  rake,
  scalePM,
  buyIn,
  drawLevels,
  currency,
  modLevels,
  selectedTab,
  isSmallScreen
}) => {
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const isHoldemMax = gameType === GameType.HoldemMax;
  const isPrizesTabSelected = selectedTab === PrizeDistributionModalTabs.prizes;
  const isBlindLevelsTabSelected = selectedTab === PrizeDistributionModalTabs.blindLevels;
  const reversedDrawLevels = drawLevels ? [...drawLevels].reverse() : [];
  const shouldShowPrizes = isPrizesTabSelected || !isSmallScreen;

  if (reversedDrawLevels.length === 0) {
    return (
      <section className={styles.emptyData}>
        <p className={styles.noDataText}>
          <Translation {...TXTCLI_No_data_available} />
        </p>
      </section>
    );
  }

  return (
    <section className={styles.gameHistory}>
      {shouldShowPrizes && (
        <div className={styles.gameInfoWrapper}>
          {!isHoldemMax && (
            <>
              <ProbabilityInfo
                buyIn={buyIn}
                rake={rake}
                currency={currency}
                drawLevels={reversedDrawLevels}
                isPlayMoney={isPlayMoney}
              />
              <TimeLimits drawLevels={reversedDrawLevels} />
            </>
          )}
          <PrizePool
            currency={currency}
            scalePM={scalePM}
            drawLevels={reversedDrawLevels}
            isHoldemMax={isHoldemMax}
            modLevels={modLevels}
          />
        </div>
      )}
      {!!drawLevels?.[0]?.stakeLevels?.length &&
        (isBlindLevelsTabSelected || !isSmallScreen) &&
        !isHoldemMax && <BlindLevel stakeLevels={drawLevels?.[0].stakeLevels} />}
    </section>
  );
};

export default GameInfo;
